import { type Accessor, type Component, createMemo, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import { Button } from "@/components/ui/button";
import { useTalentPool } from "@/domains/company/talent-pool/context/talent-pool-provider";
import { SaveFields } from "./save-fields";

/**
 * Sheet save affordances — the header "Salva/Salvato" button and the inline
 * "Modifica salvataggio" panel that expands below the profile header. Both read
 * and write the same `useTalentPool()` the card hearts use, so a save/remove on
 * either surface reflects on the other with no search refetch (D9, AC).
 *
 * The two pieces are mounted in separate sheet slots (header vs. body). The
 * enclosing `FreelanceSheet` owns the shared `open` signal and the `commitNote`
 * ref, wiring them together: the button toggles `open`, the panel forwards
 * `SaveFields`' `commit` up via `registerCommit`, and `onCollapse` flushes a
 * dirty note before collapsing — on "Fatto", on toggling "Salvato" off, and on
 * sheet close (D8). An untouched collapse issues zero `updateSaved` (guarded by
 * `SaveFields`' local dirty flag).
 */

type SheetSaveState =
  | "inert"
  | "locked"
  | "unsaved"
  | "saved-own"
  | "colleague";

export type SheetSaveButtonProps = {
  freelancerProfileId: string;
  /**
   * `search_id` della pagina di risultati del freelancer aperto nella sheet.
   * Passato SOLO quando la sheet è stata aperta dalla lista della ricerca
   * attiva; dal pool l'oggetto non lo porta e il campo non parte.
   */
  searchId?: string;
  /** Reactive read of the shared panel-open signal (owned by the sheet). */
  panelOpen: Accessor<boolean>;
  /** Expand the panel (after a resolved save, or when toggling "Salvato" on). */
  onOpen: () => void;
  /** Flush a dirty note, then collapse the panel (toggling "Salvato" off). */
  onCollapse: () => void;
};

/**
 * `SheetSaveButton` — the header "Salva/Salvato" affordance. It mirrors the card
 * heart's gating on `capabilities.status` (same A2 inert rule):
 *
 * - **inert** (`status !== "success"`): disabled "Salva" — no save, no upsell.
 * - **locked** (`success` + `enabled: false`): "Salva" + lock → opens the upsell
 *   (`source: "sheet"`), never saves.
 * - **unsaved** (`success` + `enabled` + no entry): "Salva" → `save(id, "sheet")`
 *   (con il `search_id` quando la sheet viene dalla ricerca), then expands the
 *   panel when the promise resolves (201 / 409-sync).
 * - **saved-own** (`entry.saved_by_me: true`): "Salvato" → toggles the pre-filled
 *   panel.
 * - **colleague-shared** (`entry.saved_by_me: false`): "Salvato" → toggles a
 *   read-only panel (D6); no remove affordance.
 */
export const SheetSaveButton: Component<SheetSaveButtonProps> = (props) => {
  const { t } = useI18n();
  const talentPool = useTalentPool();

  const state = createMemo<SheetSaveState>(() => {
    if (talentPool.capabilities.status !== "success") return "inert";
    if (talentPool.capabilities.enabled === false) return "locked";
    const current = talentPool.entry(props.freelancerProfileId);
    if (current === undefined) return "unsaved";
    return current.saved_by_me === false ? "colleague" : "saved-own";
  });

  const isSaved = createMemo(
    () => state() === "saved-own" || state() === "colleague",
  );
  const label = createMemo(() =>
    isSaved()
      ? t("company.talent_pool.sheet.saved")
      : t("company.talent_pool.sheet.save"),
  );
  const iconClass = createMemo(() =>
    isSaved() ? "ph-fill ph-heart" : "ph ph-heart",
  );
  const variant = createMemo<"secondary" | "outline">(() =>
    isSaved() ? "secondary" : "outline",
  );

  const handleClick = () => {
    const current = state();
    if (current === "locked") {
      talentPool.upsell.open("sheet");
      return;
    }
    if (current === "unsaved") {
      // The provider owns error handling (revert + toast / upsell routing); we
      // ignore the rejection here. The panel expands only on RESOLVE.
      void talentPool
        .save(props.freelancerProfileId, "sheet", props.searchId)
        .then(() => props.onOpen())
        .catch(() => {});
      return;
    }
    if (current === "saved-own" || current === "colleague") {
      if (props.panelOpen()) props.onCollapse();
      else props.onOpen();
      return;
    }
    // inert: no-op (belt-and-suspenders with the `disabled` attribute).
  };

  return (
    <Button
      type="button"
      size="sm"
      variant={variant()}
      disabled={state() === "inert"}
      onClick={handleClick}
      class="gap-1.5"
      classList={{ "text-gray-500": state() === "locked" }}
    >
      <i class={`${iconClass()} text-sm`} aria-hidden="true" />
      <span>{label()}</span>
      <Show when={state() === "locked"}>
        <i class="ph-fill ph-lock text-xs" aria-hidden="true" />
      </Show>
    </Button>
  );
};

export type SheetSavePanelProps = {
  freelancerProfileId: string;
  /** Reactive read of the shared panel-open signal (owned by the sheet). */
  panelOpen: Accessor<boolean>;
  /** Flush a dirty note, then collapse ("Fatto"). */
  onCollapse: () => void;
  /** Collapse after a removal (no note commit — the entry is gone). */
  onRemoved: () => void;
  /** Receives `SaveFields`' `commit`; the sheet flushes it on any dismissal. */
  registerCommit: (commit: () => void) => void;
};

/**
 * `SheetSavePanel` — the collapsible "Modifica salvataggio" panel below the
 * profile header. Its body is the shared `SaveFields` (identical note +
 * visibility behavior to the card popover, including automatic read-only for
 * colleague-shared entries — D6). The footer ("Rimuovi dal pool" / "Fatto") is
 * shown only when the entry is editable.
 */
export const SheetSavePanel: Component<SheetSavePanelProps> = (props) => {
  const { t } = useI18n();
  const talentPool = useTalentPool();

  const isReadOnly = createMemo(
    () => talentPool.entry(props.freelancerProfileId)?.saved_by_me === false,
  );

  const handleRemove = () => {
    // No note commit — the entry is being deleted.
    void talentPool.remove(props.freelancerProfileId);
    props.onRemoved();
  };

  return (
    <Show when={props.panelOpen()}>
      <section class="flex flex-col gap-4 rounded-lg border border-gray-200 bg-muted/60 p-4">
        <p class="text-sm font-semibold">
          {t("company.talent_pool.sheet.panel_title")}
        </p>

        <SaveFields
          freelancerProfileId={props.freelancerProfileId}
          registerCommit={props.registerCommit}
        />

        <Show when={!isReadOnly()}>
          <footer class="flex items-center justify-between gap-2">
            <Button
              variant="destructiveOutline"
              size="sm"
              onClick={handleRemove}
            >
              {t("company.talent_pool.sheet.remove")}
            </Button>
            <Button size="sm" onClick={props.onCollapse}>
              {t("company.talent_pool.popover.footer.done")}
            </Button>
          </footer>
        </Show>
      </section>
    </Show>
  );
};
